import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { redeemKey, storeLicenseKey } from '../services/licenseService';
import { discoverEnvironments, DiscoveredEnvironment } from '../services/discoveryService';

interface Props {
  pca: IPublicClientApplication;
  /** Called after a key is successfully redeemed + stored. Defaults to a reload. */
  onActivated?: () => void;
}

type EnvState = 'loading' | 'ready' | 'none' | 'error';

/**
 * Admin-facing Setup Wizard: resolve this tenant's Dataverse environment (via
 * the Global Discovery Service) and activate a license key. The chosen
 * environment URL is submitted with the key in a single /redeem call; the raw
 * key is then stored in the tenant's dagrunpm_LicenseKey environment variable.
 */
export default function LicenseActivation({ pca, onActivated }: Props) {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const [envState, setEnvState] = useState<EnvState>('loading');
  const [envs, setEnvs] = useState<DiscoveredEnvironment[]>([]);
  const [selectedApiUrl, setSelectedApiUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await discoverEnvironments(pca);
        if (cancelled) return;
        setEnvs(list);
        if (list.length === 0) {
          setEnvState('none');
        } else {
          // Auto-select when there's exactly one; otherwise wait for the admin.
          setSelectedApiUrl(list.length === 1 ? list[0].apiUrl : '');
          setEnvState('ready');
        }
      } catch {
        if (!cancelled) setEnvState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [pca]);

  async function activate() {
    const trimmed = key.trim();
    if (!trimmed) {
      setError('Enter a license key.');
      return;
    }
    if (!selectedApiUrl) {
      setError('Select your Dataverse environment.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // 1) Validate the key AND store the chosen environment URL in one call.
      const result = await redeemKey(pca, trimmed, selectedApiUrl);
      if (!result.ok) {
        setError('That license key could not be activated. Check the key and try again.');
        setBusy(false);
        return;
      }
      // 2) Persist the raw key into this tenant's Dataverse env variable. By now
      //    redeemKey has set the resolved environment, so this writes to it.
      await storeLicenseKey(pca, trimmed);
      setDone(true);
      // 3) Reload so the app re-runs its license check on the runtime path.
      if (onActivated) onActivated();
      else setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Activation failed.');
      setBusy(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 11px',
    fontSize: 13,
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-md)',
    background: 'var(--surface)',
    color: 'var(--ink)',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ marginTop: 20 }}>
      {/* ── Step 1: Dataverse environment ── */}
      <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>
        Dataverse environment
      </label>
      {envState === 'loading' && (
        <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: 0 }}>Finding your environments…</p>
      )}
      {envState === 'error' && (
        <p style={{ fontSize: 12, color: 'var(--red, #DC2626)', margin: 0 }}>
          Couldn’t reach the Dataverse Discovery Service. Reload to try again.
        </p>
      )}
      {envState === 'none' && (
        <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0, lineHeight: 1.6 }}>
          No Dataverse environment is provisioned for this tenant yet. Create one in the
          <strong> Power Platform admin center</strong> first, then reload this page to continue.
        </p>
      )}
      {envState === 'ready' && envs.length === 1 && (
        <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--brand)' }}>✓</span>
          <span>{envs[0].friendlyName}</span>
        </div>
      )}
      {envState === 'ready' && envs.length > 1 && (
        <select
          value={selectedApiUrl}
          onChange={(e) => setSelectedApiUrl(e.target.value)}
          disabled={busy || done}
          style={inputStyle}
        >
          <option value="">Select an environment…</option>
          {envs.map((e) => (
            <option key={e.apiUrl} value={e.apiUrl}>{e.friendlyName}</option>
          ))}
        </select>
      )}

      {/* ── Step 2: License key ── */}
      <label
        htmlFor="dagrun-license-key"
        style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', margin: '16px 0 6px' }}
      >
        License key
      </label>
      <input
        id="dagrun-license-key"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !busy) activate(); }}
        placeholder="dagrun-…"
        disabled={busy || done || envState === 'none'}
        autoComplete="off"
        spellCheck={false}
        style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
      />

      {error && <p style={{ color: 'var(--red, #DC2626)', fontSize: 12, margin: '8px 0 0' }}>{error}</p>}
      {done && <p style={{ color: 'var(--green, #16A34A)', fontSize: 12, margin: '8px 0 0' }}>Activated. Reloading…</p>}

      <button
        type="button"
        onClick={activate}
        disabled={busy || done || envState === 'none' || envState === 'loading'}
        style={{
          marginTop: 12,
          padding: '9px 18px',
          fontSize: 13,
          fontWeight: 600,
          color: '#fff',
          background: busy || done || envState === 'none' || envState === 'loading' ? 'var(--ink-4)' : 'var(--brand)',
          border: 'none',
          borderRadius: 'var(--r-md)',
          cursor: busy || done || envState === 'none' || envState === 'loading' ? 'default' : 'pointer',
        }}
      >
        {busy ? 'Activating…' : 'Activate license'}
      </button>
    </div>
  );
}
